/* ============================================
   WEBSOCKET HOOK - Real-time Server Connection
   ============================================ */

import { useEffect, useRef, useState, useCallback } from 'react';

export function useWebSocket(url) {
  const wsRef = useRef(null);
  const [status, setStatus] = useState('disconnected');
  const [lastMessage, setLastMessage] = useState(null);
  const reconnectAttempts = useRef(0);
  const maxReconnect = 5;

  const connect = useCallback(() => {
    if (!url || url === 'wss://') return;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setStatus('connected');
        reconnectAttempts.current = 0;
        console.info('[WS] Connected to', url);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastMessage(data);
        } catch {
          setLastMessage({ type: 'raw', payload: event.data });
        }
      };

      ws.onclose = () => {
        setStatus('disconnected');
        if (reconnectAttempts.current < maxReconnect) {
          reconnectAttempts.current += 1;
          setTimeout(connect, 3000 * reconnectAttempts.current);
        }
      };

      ws.onerror = (err) => {
        console.error('[WS] Error:', err);
        setStatus('error');
      };
    } catch (err) {
      console.error('[WS] Failed to connect:', err);
      setStatus('error');
    }
  }, [url]);

  const send = useCallback((data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(typeof data === 'string' ? data : JSON.stringify(data));
      return true;
    }
    return false;
  }, []);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return { status, lastMessage, send, disconnect, connect };
}
